import { Buffer } from 'buffer'
import {
  AiUser,
  MLInferenceJob,
  VoiceModel,
  VoiceModelLicense,
  VoiceModelOrigin
} from '../../types'
import { CryptoAPI } from '../crypto'

const PREFIX = 'a2g-'

export const encodeJob = (job: Pick<MLInferenceJob, 'id'>) => {
  const encrypted = CryptoAPI.encode(job.id)
  const prefixed = PREFIX + encrypted
  const base64encoded = Buffer.from(prefixed).toString('base64')

  return encodeURIComponent(base64encoded)
}

export const decodeJob = (str: string) => {
  const decoded = decodeURIComponent(str)
  const base64decoded = Buffer.from(decoded, 'base64').toString('ascii')

  if (base64decoded.startsWith(PREFIX)) {
    return CryptoAPI.decode(base64decoded.substring(PREFIX.length)).toString()
  }

  return base64decoded
}

export const generateMLInferenceJobLink = (job: MLInferenceJob) => {
  if (typeof window === 'undefined') {
    return null
  }

  const baseURL = window?.location?.origin || ''
  return `${baseURL}/conversions/${encodeJob(job)}`
}

export enum DownloadableModelType {
  ML_INFERENCE = 'mlij',
  STEM_SPLITTER = 'ssj',
  MASTERING = 'mastering',
  ML_CHOIR_JOB = 'mlcj'
}

export enum VoiceModelAnalyticsType {
  USER_TRAINED = 'user trained professional',
  USER_TRAINED_SHARED = 'user trained professional shared',
  USER_TRAINED_QVC = 'user trained qvc',
  USER_TRAINED_QVC_SHARED = 'user trained qvc shared',
  USER_TRAINED_ZERO_SHOT = 'user trained zero-shot',
  USER_TRAINED_ZERO_SHOT_SHARED = 'user trained zero-shot shared',
  USER_MERGED = 'user merged',
  USER_MERGED_SHARED = 'user merged shared',
  USER_UPLOADED = 'user uploaded',
  USER_UPLOADED_SHARED = 'user uploaded shared',
  KITS_ROYALTY_FREE = 'kits royalty free',
  KITS_INSTRUMENT = 'kits instrument',
  KITS_ARTIST = 'kits artist',
  VERIFIED_VOICE = 'verified voice',
  VOICE_DESIGNER = 'voice designer',
  UNKNOWN = 'unknown',
  VOICE_VARIANT = 'voice variant'
}

export const getModelTypeForAmplitude = (
  model?: Pick<
    VoiceModel,
    | 'isUserCreated'
    | 'origin'
    | 'license'
    | 'aiUserId'
    | 'isSubmission'
    | 'metadata'
    | 'isQuickTrain'
  >,
  user?: AiUser | null
) => {
  const { isUserCreated, origin, license, aiUserId, isSubmission, metadata, isQuickTrain } =
    model || {}
  if (metadata?.isVoiceDesigner) return VoiceModelAnalyticsType.VOICE_DESIGNER
  if (metadata?.isVoiceVariant) return VoiceModelAnalyticsType.VOICE_VARIANT
  if (isSubmission) return VoiceModelAnalyticsType.VERIFIED_VOICE
  if (isUserCreated) {
    const isShared = aiUserId !== user?.id
    if (origin === VoiceModelOrigin.MERGED) {
      return isShared
        ? VoiceModelAnalyticsType.USER_MERGED_SHARED
        : VoiceModelAnalyticsType.USER_MERGED
    }
    if (origin === VoiceModelOrigin.PTH_UPLOADED) {
      return isShared
        ? VoiceModelAnalyticsType.USER_UPLOADED_SHARED
        : VoiceModelAnalyticsType.USER_UPLOADED
    }
    if (origin === VoiceModelOrigin.REFERENCE_FILE) {
      return isShared
        ? VoiceModelAnalyticsType.USER_TRAINED_ZERO_SHOT_SHARED
        : VoiceModelAnalyticsType.USER_TRAINED_ZERO_SHOT
    }
    if (isQuickTrain) {
      return isShared
        ? VoiceModelAnalyticsType.USER_TRAINED_QVC_SHARED
        : VoiceModelAnalyticsType.USER_TRAINED_QVC
    }
    return isShared
      ? VoiceModelAnalyticsType.USER_TRAINED_SHARED
      : VoiceModelAnalyticsType.USER_TRAINED
  }

  if (license === VoiceModelLicense.ROYALTY_FREE) return VoiceModelAnalyticsType.KITS_ROYALTY_FREE
  if (license === VoiceModelLicense.INSTRUMENT) return VoiceModelAnalyticsType.KITS_INSTRUMENT
  if (license === VoiceModelLicense.ROYALTIES_APPLIED) return VoiceModelAnalyticsType.KITS_ARTIST

  return VoiceModelAnalyticsType.UNKNOWN
}
